// Por que uma mensagem não chegou ao cliente.
//
// Existe porque o painel mentia por omissão: a bolha aparecia igualzinha às
// outras, com o horário e tudo, enquanto a Meta tinha recusado a mensagem. Um
// áudio ficou quatro dias "enviado" desse jeito — quem descobriu foi o cliente,
// reclamando que nunca recebeu.
//
// O Chatwoot já sabe de tudo: recebe o status por webhook e guarda em
// `status: 'failed'` + `content_attributes.external_error`. Só faltava mostrar.
//
// O erro cru é ininteligível para quem atende ("131047" não diz nada). Aqui ele
// vira uma frase que diz o que fazer a seguir — e o código continua acessível no
// tooltip, para quando alguém precisar investigar.

import type { ChatwootMessage } from '@/api/endpoints/support';

export interface FalhaEnvio {
  /** Frase curta, na bolha. */
  resumo: string;
  /** O erro cru da Meta, no tooltip — para diagnóstico. */
  detalhe: string;
}

// Códigos da Cloud API que aparecem de fato no nosso atendimento.
// Referência: developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes
const POR_CODIGO: Record<string, string> = {
  '131053': 'A Meta não conseguiu baixar o arquivo',
  '131052': 'A Meta não conseguiu baixar a mídia',
  '131047': 'Passaram 24h da última mensagem do cliente',
  '131026': 'Este número não recebe mensagens no WhatsApp',
  '131049': 'A Meta limitou o envio para este cliente agora',
  '131051': 'Tipo de mensagem não suportado',
  '130472': 'Cliente fora do experimento de mensagens da Meta',
  '133010': 'O número do suporte não está registrado na Meta',
  '470': 'Passaram 24h da última mensagem do cliente',
};

// Quando não há código conhecido, a própria Meta às vezes manda texto legível.
const POR_TEXTO: [RegExp, string][] = [
  [
    /does ?n.t exists?|not exists? on whatsapp|not a valid whatsapp/i,
    'Este número não recebe mensagens no WhatsApp',
  ],
  [/opted? ?out|blocked/i, 'O cliente bloqueou ou saiu da conversa'],
  [/template not found|invalid template/i, 'Template inexistente ou com nome errado'],
  [/re-?engagement|24 hours/i, 'Passaram 24h da última mensagem do cliente'],
  [/media/i, 'Problema com o arquivo enviado'],
  [/rate limit|too many/i, 'Limite de envio da Meta atingido'],
];

/**
 * Descreve por que a mensagem não chegou, ou `null` se ela está bem.
 *
 * Só mensagens NOSSAS podem falhar no envio: as do cliente já chegaram por
 * definição. Notas internas nunca saem daqui, então também não falham.
 */
export function descreverFalha(msg: ChatwootMessage): FalhaEnvio | null {
  if (msg.status !== 'failed') return null;
  if (msg.message_type !== 1 || msg.private) return null;

  const cru = String(msg.content_attributes?.external_error || '').trim();
  const codigo = cru.match(/\b(\d{3,6})\b/)?.[1];

  const conhecido =
    (codigo && POR_CODIGO[codigo]) ||
    POR_TEXTO.find(([re]) => re.test(cru))?.[1] ||
    'A mensagem não chegou ao cliente';

  return { resumo: conhecido, detalhe: cru || 'A Meta não informou o motivo' };
}
